import { useMutation, useQuery } from '@apollo/client';
import {
    faArrowRotateRight,
    faExternalLink,
    faPencil,
    faPlus,
    faSpinnerThird,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import classnames from '../../../core/utils/classnames.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Modal from '../../../core/components/Modal.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import {
    CUSTOMER_INTEGRATION_ERP_PATH,
    CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_ADD_PATH,
    CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_EDIT_PATH,
    CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH,
} from '../../constants.ts';
import {
    CREATE_MICROTECH_BUERO_PLUS_INTEGRATION,
    DELETE_MICROTECH_BUERO_PLUS_INTEGRATION,
    GET_MICROTECH_BUERO_PLUS_INTEGRATIONS,
    TRIGGER_MICROTECH_BUERO_PLUS_INTEGRATION_MASTERDATA_UPDATE,
} from '../../queries.ts';
import { url } from '../../../core/utils/link.ts';

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);
const RefreshIcon = withIcon(faArrowRotateRight);

const FORM_DEFAULT_VALUES = {
    name: '',
    baseUrl: '',
    clientId: '',
    clientSecret: '',
    orderTransactionType: '',
    rfqTransactionType: '',
    offerErpSyncTransactionType: '',
    orderDocumentSysArt: '',
    rfqDocumentSysArt: '',
    orderChannelIds: [],
    rfqChannelIds: [],
};

const MicrotechBueroPlusFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH)}>
                            {t('microtechBueroPlus.form.cancelButton')}
                        </Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('microtechBueroPlus.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="baseUrl"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.baseUrl')}
                            helpText={t('microtechBueroPlus.form.baseUrlHelp')}
                            error={formErrors.baseUrl}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="clientId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('microtechBueroPlus.form.clientId')} error={formErrors.clientId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="clientSecret"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('microtechBueroPlus.form.clientSecret')} error={formErrors.clientSecret}>
                            <StringField {...field} type="password" />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderTransactionType"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.orderTransactionType')}
                            helpText={t('microtechBueroPlus.form.orderTransactionTypeHelp')}
                            error={formErrors.orderTransactionType}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="rfqTransactionType"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.rfqTransactionType')}
                            helpText={t('microtechBueroPlus.form.rfqTransactionTypeHelp')}
                            error={formErrors.rfqTransactionType}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="offerErpSyncTransactionType"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.offerErpSyncTransactionType')}
                            helpText={t('microtechBueroPlus.form.offerErpSyncTransactionTypeHelp')}
                            error={formErrors.offerErpSyncTransactionType}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderDocumentSysArt"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.orderDocumentSysArt')}
                            helpText={t('microtechBueroPlus.form.orderDocumentSysArtHelp')}
                            error={formErrors.orderDocumentSysArt}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.orderChannelIds')}
                            error={formErrors.orderChannelIds}
                        >
                            <CheckboxGroup
                                {...field}
                                options={orderChannels.map((channel) => ({
                                    value: channel.id,
                                    label: channel.name,
                                }))}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="rfqDocumentSysArt"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('microtechBueroPlus.form.rfqDocumentSysArt')}
                            helpText={t('microtechBueroPlus.form.rfqDocumentSysArtHelp')}
                            error={formErrors.rfqDocumentSysArt}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="rfqChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('microtechBueroPlus.form.rfqChannelIds')} error={formErrors.rfqChannelIds}>
                            <CheckboxGroup
                                {...field}
                                options={rfqChannels.map((channel) => ({
                                    value: channel.id,
                                    label: channel.name,
                                }))}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export const AddMicrotechBueroPlusFormModal = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_MICROTECH_BUERO_PLUS_INTEGRATION, {
        refetchQueries: [GET_MICROTECH_BUERO_PLUS_INTEGRATIONS],
    });

    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    baseUrl: formData.baseUrl,
                    clientId: formData.clientId,
                    clientSecret: formData.clientSecret,
                    orderTransactionType: formData.orderTransactionType,
                    rfqTransactionType: formData.rfqTransactionType,
                    offerErpSyncTransactionType: formData.offerErpSyncTransactionType,
                    orderDocumentSysArt: formData.orderDocumentSysArt,
                    rfqDocumentSysArt: formData.rfqDocumentSysArt,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });
            publishToast({ description: t('microtechBueroPlus.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('microtechBueroPlus.createError'), status: 'error' });
        }
    };

    return (
        <MicrotechBueroPlusFormModal
            title={t('microtechBueroPlus.form.createTitle')}
            submitButtonLabel={t('microtechBueroPlus.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH))}
        />
    );
};

const MicrotechBueroPlusRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_MICROTECH_BUERO_PLUS_INTEGRATION, {
        refetchQueries: [GET_MICROTECH_BUERO_PLUS_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('microtechBueroPlus.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('microtechBueroPlus.deleteError'), status: 'error' });
        }
    };

    const [triggerMasterDataUpdate] = useMutation(TRIGGER_MICROTECH_BUERO_PLUS_INTEGRATION_MASTERDATA_UPDATE);
    const handleTriggerMasterDataUpdate = async () => {
        try {
            await triggerMasterDataUpdate({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('microtechBueroPlus.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('microtechBueroPlus.updateMasterDataError'), status: 'error' });
        }
    };

    const channels = [...(integration?.orderChannels ?? []), ...(integration?.rfqChannels ?? [])];

    return (
        <Table.Row>
            <Table.Cell>{integration.name}</Table.Cell>
            <Table.Cell>
                <span className="text-content-strong">
                    {t('microtechBueroPlus.authState', {
                        authState: integration.authState,
                    })}
                </span>
            </Table.Cell>
            <Table.Cell>
                <div className="flex gap-1.5 flex-wrap">
                    {channels.map((channel) => (
                        <span
                            key={channel.id}
                            className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                        >
                            {channel.name}
                        </span>
                    ))}
                </div>
            </Table.Cell>
            <Table.Cell>
                {integration.masterDataUpdateActive ? (
                    <span className="flex gap-2 items-center animate-pulse">
                        <span className="text-content-success-strong">
                            <LoadingIcon spin />
                        </span>
                        <span>
                            {`${t('microtechBueroPlus.masterDataUpdateInProgress')} ${(
                                (integration.fetchProgress ?? 0) * 100
                            ).toFixed(2)}%`}
                        </span>
                    </span>
                ) : integration.masterDataUpdateError ? (
                    <div className="flex flex-col gap-1">
                        <span className="text-content-danger-strong">
                            {t('microtechBueroPlus.masterDataUpdateError')}
                        </span>
                        <span className="text-content-medium text-sm whitespace-pre-line">
                            {integration.masterDataUpdateError}
                        </span>
                    </div>
                ) : integration.masterDataUpdatedAt ? (
                    <span>
                        {t('microtechBueroPlus.lastMasterDataUpdate', {
                            updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
                        })}
                    </span>
                ) : (
                    <span className="text-content-medium">—</span>
                )}
            </Table.Cell>
            <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
            <Table.Cell sticky="right">
                <div className="flex gap-2">
                    <RowControlIconButton onClick={handleTriggerMasterDataUpdate}>
                        <RefreshIcon />
                    </RowControlIconButton>

                    <RowControlIconButton asChild>
                        <Link
                            to={url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_EDIT_PATH, {
                                integrationId: integration.id,
                            })}
                            className="!text-content-strong hover:!text-content-strong"
                        >
                            <EditIcon />
                        </Link>
                    </RowControlIconButton>

                    <RowControlIconButton
                        className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                        onClick={handleDeleteIntegration}
                    >
                        <DeleteIcon />
                    </RowControlIconButton>
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

const MicrotechBueroPlusIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const { data, loading, refetch } = useQuery(GET_MICROTECH_BUERO_PLUS_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.microtechBueroPlusIntegrations || [];

    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')}`;

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('microtechBueroPlus.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_ADD_PATH)}>
                                <AddIcon />
                                {t('microtechBueroPlus.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">
                                    {t('microtechBueroPlus.table.name')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('microtechBueroPlus.table.authState')}</Table.HeadCell>
                                <Table.HeadCell>{t('microtechBueroPlus.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('microtechBueroPlus.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell>{t('microtechBueroPlus.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <MicrotechBueroPlusRow
                                        key={integration.id}
                                        integration={integration}
                                        refetch={refetch}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center text-content-medium">
                                        {t('microtechBueroPlus.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default MicrotechBueroPlusIntegrationPage;
